import { ChangeDetectionStrategy, Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { filter, first, take } from 'rxjs/operators';
import { SettingsService } from '../core/settings.service';
import { ITickmark } from '../form-elements/range/range.component';
import { IToggleButtonsGroupItem } from '../form-elements/toggle-buttons-group/toggle-buttons-group';
import { FormService } from '../form/form.service';
import { LoanService } from './loan.service';

const loanMonthTerms: ITickmark[] = [
    { label: '24', value: 24 },
    { label: '36', value: 36 },
    { label: '48', value: 48 },
    { label: '60', value: 60 },
    { label: '72', value: 72 },
    { label: '84', value: 84, isDefault: true },
    { label: '96', value: 96 },
];

const downPaymentTerms: ITickmark[] = [
    { label: '20%', value: 20, isDefault: true },
    { label: '30%', value: 30 },
    { label: '40%', value: 40 },
];

const isFixedInterestRadioGroup: IToggleButtonsGroupItem[] = [
    { label: 'Fast', value: 'true' },
    { label: 'Variabel', value: 'false' },
];

@Component({
    selector: 'app-loan',
    template: `
        <section class="loan" [ngClass]="{
            'loan--loading': !!(loanService.isLoading$ | async),
            'alt-theme': this.altTheme
        }">
            <header *ngIf="showHeader" class="flow-step__section-block-header">
                <h5>{{ 'loan.calculator____headline' | translate }}</h5>
                <p>{{ 'loan.calculator____description' | translate }}</p>
            </header>
            <form *ngIf="form" class="loan-form" [formGroup]="form" (ngSubmit)="onSubmit()">
                <div class="loan__input-section">
                    <!-- INTEREST -->
                    <app-toggle-buttons-group *ngIf="currentInterestType"
                        [parentForm]="form"
                        controlName="isFixedInterest"
                        [active]="currentInterestType"
                        [group]="isFixedInterestRadioGroup"
                        (handleValueChange)="handleInterestChange($event)"
                    ></app-toggle-buttons-group>
                </div>
                <div class="loan__input-section" *ngIf="!loanAmount">
                    <ng-container *ngIf="settingsService.get() | async as ss">
                        <app-input-range
                            class="user-info-input"
                            controlName="loanAmount"
                            [parentForm]="form"
                            [label]="'loan.base_amount' | translate"
                            [labelUnit]="loanAmountLabelValue || loanAmountSetFromRange | defaultcurrency"
                            [minVal]="ss.loanCalculator?.loanAmountRangeMinimumValue || 0"
                            [minValLabel]="ss.loanCalculator?.loanAmountRangeMinimumValue || 0 | defaultcurrency"
                            [maxVal]="ss.loanCalculator?.loanAmountRangeMaximumValue || 1000000"
                            [maxValLabel]="ss.loanCalculator?.loanAmountRangeMaximumValue || 0 | defaultcurrency"
                            (handleValueChange)="handleLoanAmountChange($event)"
                            (liveOnChangeValueUseWithCautionTriggersOnEverySingleValueChange)="handleLoanAmountLabelChange($event)"
                            cssClasses="input__input-element--white-background"
                        ></app-input-range>
                    </ng-container>
                </div>
                <div class="loan__input-section">
                    <app-input-range
                        class="user-info-input"
                        controlName="montlyTerms"
                        [parentForm]="form"
                        [label]="'loan.monthly_terms' | translate"
                        [labelUnit]="'Måneder'"
                        cssClasses="input__input-element--white-background"
                        (handleValueChange)="handleMonthTermChange($event)"
                        [tickmarks]="loanMonthTerms"
                    ></app-input-range>
                </div>
                <div class="loan__input-section">
                    <app-input-range
                        class="user-info-input"
                        controlName="dpTerms"
                        [parentForm]="form"
                        [label]="'loan.down_payment' | translate"
                        cssClasses="input__input-element--white-background"
                        [tickmarks]="downPaymentTerms"
                        [labelUnit]="downpaymentAmount | defaultcurrency"
                        (handleValueChange)="handleDPChange($event)"
                    ></app-input-range>
                </div>
            </form>

            <app-loan-error></app-loan-error>
        </section>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoanComponent implements OnChanges, OnInit {
    static ref = 'loan-form';
    @Input() loanAmount?: number;
    @Input() registrationDate?: string | null | undefined;
    @Input() initialRangeValue?: number;
    @Input() downPaymentPct?: number;
    @Input() paymentTerms?: number;
    @Input() interestType?: string | null;
    @Input() showHeader = true;
    @Input() altTheme = false;
    isFixedInterestRadioGroup = isFixedInterestRadioGroup;
    form?: UntypedFormGroup;
    currentLoanMonthTerms?: ITickmark;
    currentDownPaymentTerms?: ITickmark;
    currentInterestType?: IToggleButtonsGroupItem = isFixedInterestRadioGroup[0];
    loanMonthTerms?: ITickmark[];
    downPaymentTerms?: ITickmark[];
    loanAmountSetFromRange?: number;
    loanAmountLabelValue?: number;
    previousLoanData: any = {};

    get _loanAmount() {
        return this.loanAmount ?? this.loanAmountSetFromRange;
    }

    get downpaymentAmount() {
        const terms = this.currentDownPaymentTerms?.value && this._loanAmount && this._loanAmount * (this.currentDownPaymentTerms?.value / 100);
        return Math.ceil(terms ?? 0);
    }

    constructor(
        private readonly fb: UntypedFormBuilder,
        public readonly loanService: LoanService,
        readonly settingsService: SettingsService
    ) {}

    ngOnInit() {
        // Update each interest label based on the incoming loan data
        this.loanService.interestValues$.pipe(
            filter((data: any) => data && Object.keys(data).length > 0), // Filter out empty or invalid data
            take(1) // Take the first valid emission
        ).subscribe((data: any) => {
            this.isFixedInterestRadioGroup.forEach((item) => {
                if (item.label && data.variable) {
                    if (item.label.includes('Fast')) {
                        item.label = `Fast ${data.fixed}%`;
                    } else if (item.label.includes('Variabel')) {
                        item.label = `Variabel ${data.variable}%`;
                    }
                }
            });
        });

        this.settingsService
            .get()
            .pipe(first())
            .subscribe(({ loanCalculator: { loanAmountRangeDefaultValue } }) => {
                const defaultRangeVal = this.initialRangeValue || loanAmountRangeDefaultValue;
                this.loanAmountSetFromRange = defaultRangeVal;
                this.form = this.fb.group(
                    {
                        isFixedInterest: [],
                        loanAmount: [defaultRangeVal],
                        montlyTerms: [],
                        dpTerms: [],
                    },
                    {
                        updateOn: 'change',
                    }
                );
                this.setInterestType();
            });

        // Set default values
        this.setLoanMonthTerms();
        this.setDownPaymentTerms();
    }

    setLoanMonthTerms() {
        const anyMatch = loanMonthTerms.find((m) => m.value === this.paymentTerms);

        this.loanMonthTerms = anyMatch
            ? loanMonthTerms.map(({ label, value }) => ({ label, value, isDefault: value === this.paymentTerms }))
            : loanMonthTerms;
    }

    setDownPaymentTerms() {
        const roundedDownPaymentPct = Math.floor(this.downPaymentPct || 0);
        const anyMatch = downPaymentTerms.find((t) => t.value === roundedDownPaymentPct);

        this.downPaymentTerms = anyMatch
            ? downPaymentTerms.map(({ label, value }) => ({ label, value, isDefault: value === roundedDownPaymentPct }))
            : downPaymentTerms;
    }

    setInterestType() {
        const newInterestType = this.interestType === 'Fast' ? 'true' : 'false';
        const matchedVal = isFixedInterestRadioGroup.find(({ value }) => value === newInterestType);

        if (matchedVal) {
            this.form?.controls['isFixedInterest'].setValue(matchedVal.value);
            this.currentInterestType = matchedVal;
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        // recalculating loan
        if (changes.loanAmount) {
            this.submitIfChanged();
        }
        if (changes.paymentTerms) {
            this.setLoanMonthTerms();
        }
        if (changes.downPaymentPct) {
            this.setDownPaymentTerms();
        }
        if (changes.interestType) {
            this.setInterestType();
        }
    }

    handleLoanAmountChange({ value }: ITickmark) {
        this.loanAmountSetFromRange = value;
        this.submitIfChanged();
    }

    handleLoanAmountLabelChange(value: number) {
        this.loanAmountLabelValue = value;
    }

    handleMonthTermChange(val?: ITickmark) {
        this.currentLoanMonthTerms = val;
        this.submitIfChanged();
    }

    handleDPChange(val?: ITickmark) {
        this.currentDownPaymentTerms = val;
        this.submitIfChanged();
    }

    handleInterestChange(value: IToggleButtonsGroupItem) {
        this.currentInterestType = value;
        this.submitIfChanged();
    }

    private submitIfChanged() {
        const currentLoanData = {
            objectPrice: this._loanAmount,
            downPayment: this.downpaymentAmount,
            paymentTerms: this.currentLoanMonthTerms?.value,
            isFixedInterest: this.currentInterestType?.value === 'true',
            registrationDate: this.registrationDate
        };

        if (this.form?.invalid || this.form?.disabled) {
            FormService.markControlsAsTouched(this.form);
            return;
        }

        if (!this.hasLoanDataChanged(currentLoanData)) {
            return;
        }

        this.previousLoanData = { ...currentLoanData };  // Update previousLoanData to the current state
        this.onSubmit();
    }

    private isEmpty(value: unknown) {
        return value === null || value === undefined || value === '';
    }

    private hasLoanDataChanged(newLoanData: any): boolean {
        return JSON.stringify(this.previousLoanData) !== JSON.stringify(newLoanData);
    }

    onSubmit() {
        const objectPrice = this._loanAmount;
        const downPayment = this.downpaymentAmount;
        const paymentTerms = this.currentLoanMonthTerms?.value;
        const isFixedInterest = this.currentInterestType?.value === 'true';
        const registrationDate = this.registrationDate;

        if (this.isEmpty(objectPrice)) return;
        if (!downPayment) return;
        if (this.isEmpty(paymentTerms)) return;
        if (registrationDate === null || registrationDate === '') return;

        this.loanService.fetchLoanData({
            objectPrice,
            downPayment,
            paymentTerms,
            isFixedInterest,
            registrationDate
        });
    }
}
